import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Smartphone, Monitor, Tablet, Trash2, Bell, BellOff, Users } from "lucide-react";

const iconFor = (platform, label = "") => {
  const t = (label || "").toLowerCase();
  if (t.includes("ipad") || t.includes("tablet")) return Tablet;
  if (t.includes("iphone") || t.includes("android")) return Smartphone;
  return Monitor;
};

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("sv-SE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return s.slice(0, 16);
  }
};

export function AllDevicesPanel() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/push/devices/all");
      setDevices(data.devices || []);
    } catch (e) {
      toast.error("Kunde inte hämta enheter");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggle = async (d) => {
    try {
      await api.patch(`/push/devices/all/${d.id}`, { enabled: !d.enabled });
      toast.success(!d.enabled ? "Push aktiverad" : "Push pausad");
      load();
    } catch (e) {
      toast.error("Kunde inte uppdatera");
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Ta bort enheten "${d.label || "Namnlös"}" för ${d.user_name || "?"}? De måste registrera push på nytt.`)) return;
    try {
      await api.delete(`/push/devices/all/${d.id}`);
      toast.success("Enhet borttagen");
      load();
    } catch (e) {
      toast.error("Kunde inte ta bort");
    }
  };

  if (loading) return <p className="text-xs text-[color:var(--text-tertiary)]">Laddar…</p>;
  if (devices.length === 0) {
    return (
      <p className="text-xs text-[color:var(--text-tertiary)] italic">
        Inga registrerade enheter i hela familjen ännu.
      </p>
    );
  }

  // Gruppera per användare
  const groups = devices.reduce((acc, d) => {
    const key = d.user_id || "okänd";
    if (!acc[key]) acc[key] = { name: d.user_name || "Okänd", email: d.user_email, is_child: d.user_is_child, devices: [] };
    acc[key].devices.push(d);
    return acc;
  }, {});

  return (
    <div className="space-y-4" data-testid="all-devices-list">
      {Object.entries(groups).map(([uid, g]) => (
        <div key={uid} className="space-y-1.5">
          <div className="flex items-center gap-2 px-1">
            <Users size={12} className="text-[color:var(--text-tertiary)]" />
            <p className="text-xs font-medium text-[color:var(--text-secondary)]">{g.name}</p>
            {g.is_child && (
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#F0F6E6] text-[color:var(--moss)] font-semibold">
                Barn
              </span>
            )}
            <span className="text-[10px] text-[color:var(--text-tertiary)] ml-auto">
              {g.devices.filter((d) => d.enabled).length}/{g.devices.length} aktiva
            </span>
          </div>
          {g.devices.map((d) => {
            const Icon = iconFor(d.platform, d.label);
            return (
              <div
                key={d.id}
                className={`flex items-center gap-3 p-3 rounded-xl border ${d.enabled ? "bg-white border-[color:var(--border)]" : "bg-[#FAFAF7] border-[color:var(--border-subtle)] opacity-70"}`}
                data-testid={`admin-device-row-${d.id}`}
              >
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${d.enabled ? "bg-[#EAF3EA] text-[color:var(--moss)]" : "bg-[#F0EDE5] text-[color:var(--text-tertiary)]"}`}>
                  <Icon size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{d.label || "Namnlös enhet"}</p>
                  <p className="text-[11px] text-[color:var(--text-tertiary)] truncate">
                    {d.platform} · {d.enabled ? "Aktiv" : "Pausad"} · senast {fmtDate(d.last_seen_at || d.updated_at)}
                  </p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button
                    onClick={() => toggle(d)}
                    className={`w-8 h-8 rounded-lg flex items-center justify-center hover:bg-[#F0EDE5] ${d.enabled ? "text-[color:var(--moss)]" : "text-[color:var(--text-tertiary)]"}`}
                    title={d.enabled ? "Pausa push" : "Aktivera push"}
                    data-testid={`admin-device-toggle-${d.id}`}
                  >
                    {d.enabled ? <Bell size={13} /> : <BellOff size={13} />}
                  </button>
                  <button
                    onClick={() => remove(d)}
                    className="w-8 h-8 rounded-lg hover:bg-[#FEE2E2] flex items-center justify-center text-[color:var(--text-tertiary)] hover:text-[#D44A3A]"
                    title="Ta bort enhet"
                    data-testid={`admin-device-delete-${d.id}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
